import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/api/queryKeys';
import { formsApi } from '../api/formsApi';
import type {
  FormApproveInput,
  FormMarkIncompleteInput,
  FormResendInput,
  FormSubmissionWrite,
} from '../api/types';

export function useFormSubmission(id: string | null | undefined) {
  return useQuery({
    queryKey: keys.forms.submission(id ?? ''),
    queryFn: ({ signal }) => formsApi.getSubmission(id!, { signal }),
    enabled: id != null && id !== '',
    staleTime: 60_000,
  });
}

export function useSubmissionDocument(submissionId: string | null | undefined) {
  return useQuery({
    queryKey: keys.forms.submissionDocument(submissionId ?? ''),
    queryFn: ({ signal }) => formsApi.getSubmissionDocument(submissionId!, { signal }),
    enabled: submissionId != null && submissionId !== '',
    staleTime: 60_000,
  });
}

export function useCreateStaffSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) => formsApi.createSubmission({ assignment_id: assignmentId }),
    onSuccess: (data) => {
      const id = data.id ?? data.submission_id;
      if (id) void queryClient.invalidateQueries({ queryKey: keys.forms.submission(id) });
    },
  });
}

export function usePatchStaffSubmission(submissionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FormSubmissionWrite) => formsApi.patchSubmission(submissionId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.submission(submissionId) });
      void queryClient.invalidateQueries({ queryKey: ['forms', 'patient-form-detail'] });
    },
  });
}

export function useSubmitStaffSubmission(submissionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FormSubmissionWrite) => formsApi.submitSubmission(submissionId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.submission(submissionId) });
      void queryClient.invalidateQueries({ queryKey: keys.forms.queue });
    },
  });
}

function useReviewInvalidation(patientId?: number | null) {
  const queryClient = useQueryClient();
  return (submissionId: string) => {
    void queryClient.invalidateQueries({ queryKey: keys.forms.submission(submissionId) });
    void queryClient.invalidateQueries({ queryKey: keys.forms.submissionDocument(submissionId) });
    void queryClient.invalidateQueries({ queryKey: keys.forms.queue });
    void queryClient.invalidateQueries({ queryKey: keys.formsStats });
    void queryClient.invalidateQueries({ queryKey: ['forms', 'patient-form-detail'] });
    if (patientId != null) {
      void queryClient.invalidateQueries({ queryKey: keys.patientForms(patientId) });
    }
  };
}

export function useApproveSubmission(patientId?: number | null) {
  const invalidate = useReviewInvalidation(patientId);
  return useMutation({
    mutationFn: ({ submissionId, body }: { submissionId: string; body: FormApproveInput }) =>
      formsApi.approveSubmission(submissionId, body),
    onSuccess: (_data, { submissionId }) => {
      invalidate(submissionId);
    },
  });
}

/**
 * Mark incomplete is assignment-scoped (D25/D28) — it closes a `pending` paper
 * assignment with no submission, which submission-scoped actions cannot reach.
 */
export function useMarkIncompleteAssignment(patientId?: number | null) {
  const invalidate = useReviewInvalidation(patientId);
  return useMutation({
    mutationFn: (vars: {
      assignmentId: string;
      submissionId?: string;
      body?: FormMarkIncompleteInput;
    }) => formsApi.markIncompleteAssignment(vars.assignmentId, vars.body),
    onSuccess: (_data, { submissionId }) => {
      if (submissionId) invalidate(submissionId);
    },
  });
}

export function useResendSubmission(patientId?: number | null) {
  const invalidate = useReviewInvalidation(patientId);
  return useMutation({
    mutationFn: ({ submissionId, body }: { submissionId: string; body: FormResendInput }) =>
      formsApi.resendSubmission(submissionId, body),
    onSuccess: (_data, { submissionId }) => {
      invalidate(submissionId);
    },
  });
}

/** Reminder for a sent form the patient has not opened — there is no submission yet. */
export function useResendAssignment(patientId?: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assignmentId, body }: { assignmentId: string; body: FormResendInput }) =>
      formsApi.resendAssignment(assignmentId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['forms', 'assignments'] });
      void queryClient.invalidateQueries({ queryKey: ['patient', 'forms'] });
      if (patientId != null) {
        void queryClient.invalidateQueries({ queryKey: keys.patientForms(patientId) });
      }
    },
  });
}

export function useRetrySubmissionDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (submissionId: string) => formsApi.retrySubmissionDocument(submissionId),
    onSuccess: (_data, submissionId) => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.submissionDocument(submissionId) });
      void queryClient.invalidateQueries({ queryKey: keys.forms.submission(submissionId) });
    },
  });
}
